import type { Message } from '@copilotkit/shared';
import type { SerializableMessage } from '@/pages/AIChatCopilotKit/types';

/**
 * Sanitize messages for Redux storage by removing non-serializable properties
 *
 * Removes: generativeUI (runtime function), state (complex objects)
 * Keeps everything else: id, role, content, name, toolCalls, toolCallId, error, image, agentName, toolName
 *
 * @param messages - Array of messages from CopilotKit
 * @returns Sanitized messages safe for Redux storage
 */
export const sanitizeMessagesForRedux = (
  messages: Message[]
): SerializableMessage[] => {
  return messages.map((msg, index) => {
    try {
      // Remove non-serializable properties at runtime
      // TypeScript knows SerializableMessage = Omit<Message, 'generativeUI' | 'state'>
      // so this is type-safe
      if ('generativeUI' in msg) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { generativeUI, ...rest } = msg;
        if ('state' in rest) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { state, ...sanitized } = rest;
          return sanitized as SerializableMessage;
        }
        return rest as SerializableMessage;
      }
      if ('state' in msg) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { state, ...sanitized } = msg;
        return sanitized as SerializableMessage;
      }
      return msg as SerializableMessage;
    } catch (error) {
      console.error(
        `[sanitizeMessages] Failed to sanitize message at index ${index}:`,
        {
          messageId: msg.id,
          messageRole: msg.role,
          error,
        }
      );
      // Fallback: Use Object.entries to strip non-serializable properties
      const sanitized = Object.fromEntries(
        Object.entries(msg).filter(
          ([key]) => key !== 'generativeUI' && key !== 'state'
        )
      );
      return sanitized as SerializableMessage;
    }
  });
};
